import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';
import { Eye, EyeOff, Loader2, AlertCircle, CheckCircle, User, Mail, Lock } from 'lucide-react';

interface XuiMigrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (user: any) => void;
  username: string;
  email: string;
  userInfo?: {
    username: string;
    email: string;
    member_group_id: number;
    status: number;
  };
}

const XuiMigrationModal: React.FC<XuiMigrationModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  username,
  email,
  userInfo
}) => {
  const [formData, setFormData] = useState({
    email: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<'info' | 'password' | 'success'>('info');

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError(null);
  };

  const validateForm = () => {
    if (!formData.email || !formData.email.includes('@')) {
      setError('Por favor, informe um email válido');
      return false;
    }
    
    // Validar formato de email mais rigorosamente
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Por favor, informe um email válido');
      return false;
    }
    
    if (!formData.newPassword) {
      setError('Nova senha é obrigatória');
      return false;
    }
    if (formData.newPassword.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres');
      return false;
    }
    if (formData.newPassword !== formData.confirmPassword) {
      setError('As senhas não coincidem');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { apiService } = await import('../../services/api');
      const response = await apiService.migrateXuiUser({
        username: userInfo?.username || username,
        email: formData.email,
        newPassword: formData.newPassword,
        confirmPassword: formData.confirmPassword
      });
      
      // Aguardar um pouco para garantir que o backend processou completamente
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Salvar token no localStorage manualmente para garantir sincronização
      if (response.token) {
        const obj = {"t": response.token, "l": true};
        localStorage.setItem("_pv001", JSON.stringify(obj));
      }
      
      // Mostrar tela de sucesso
      setStep('success');
    } catch (err: any) {
      console.error('Erro na migração XUI:', err);
      if (err.response?.status === 400) {
        setError(err.response.data?.error || 'Dados inválidos. Verifique o email e tente novamente.');
      } else if (err.response?.status === 409) {
        setError('Usuário já possui conta no CMS. Faça login normalmente.');
      } else {
        setError(err.message || 'Erro ao criar conta no CMS. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({ email: '', newPassword: '', confirmPassword: '' });
    setError(null);
    setStep('info');
    onClose();
  };

  const handleSuccessContinue = () => {
    onSuccess({ username: userInfo?.username || username });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50">
      <div className="w-full max-w-md max-h-[90vh] overflow-y-auto">
        <Card className="w-full shadow-xl bg-white border border-gray-200">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 p-3 rounded-full bg-blue-100">
            <User className="h-8 w-8 text-blue-600" />
          </div>
          <CardTitle className="text-xl">Primeiro Acesso ao CMS</CardTitle>
          <CardDescription>
            Configure sua senha para acessar o sistema
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          {step === 'info' && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="text-sl font-medium text-blue-800 mb-1">
                      Usuário XUI Encontrado
                    </h3>
                    <p className="text-sl text-blue-700">
                      Seus dados foram encontrados no sistema IPTV. Agora você pode criar uma conta no CMS.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <User className="h-4 w-4 text-gray-500" />
                  <span className="text-sl text-gray-600">
                    <strong>Usuário:</strong> {userInfo?.username || username}
                  </span>
                </div>
                {userInfo?.member_group_id && (
                  <div className="flex items-center space-x-3">
                    <div className="h-4 w-4 text-gray-500 flex items-center justify-center">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    </div>
                    <span className="text-sl text-gray-600">
                      <strong>Grupo:</strong> {userInfo.member_group_id === 1 ? 'Administrador' : 
                                            userInfo.member_group_id === 2 ? 'Sócio' :
                                            userInfo.member_group_id === 3 ? 'Master' :
                                            userInfo.member_group_id === 4 ? 'Revendedor' :
                                            userInfo.member_group_id === 6 ? 'Sub-revendedor' : 'Usuário'}
                    </span>
                  </div>
                )}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-start">
                    <Mail className="h-4 w-4 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-blue-800 font-medium">
                        Confirmação de Identidade
                      </p>
                      <p className="text-md text-blue-600 mt-1">
                        Para sua segurança, você precisará informar o email cadastrado no sistema IPTV na próxima etapa.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start">
                  <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="text-sm font-medium text-amber-800 mb-1">
                      Importante
                    </h3>
                    <p className="text-sl text-amber-700">
                      Você precisará criar uma nova senha específica para o CMS. 
                      Esta senha será diferente da sua senha do XUI Panel.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex space-x-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button
                  type="button"
                  onClick={() => setStep('password')}
                  className="flex-1"
                >
                  Continuar
                </Button>
              </div>
            </div>
          )}

          {step === 'password' && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email de Confirmação</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="Digite o email cadastrado no sistema IPTV"
                  className="w-full"
                />
                <p className="text-xs text-gray-500">
                  Digite o email exatamente como está cadastrado no sistema IPTV para confirmar sua identidade.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="newPassword">Nova Senha</Label>
                <div className="relative">
                  <Input
                    id="newPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.newPassword}
                    onChange={(e) => handleInputChange('newPassword', e.target.value)}
                    placeholder="Digite sua nova senha"
                    className="w-full pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirmar Senha</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                    placeholder="Confirme sua nova senha"
                    className="w-full pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              {error && (
                <div className="flex items-center space-x-2 text-red-600 text-sm">
                  <AlertCircle className="h-4 w-4" />
                  <span>{error}</span>
                </div>
              )}

              <div className="flex space-x-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep('info')}
                  className="flex-1 h-10"
                >
                  Voltar
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex-1 h-10 bg-green-600 hover:bg-green-700 disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Criando...
                    </>
                  ) : (
                    <>
                      <Lock className="h-4 w-4 mr-2" />
                      Criar Conta
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}

          {step === 'success' && (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="text-sm font-medium text-green-800 mb-1">
                      Conta Criada com Sucesso!
                    </h3>
                    <p className="text-sm text-green-700">
                      Sua conta no CMS foi criada com sucesso. Agora você pode acessar todas as funcionalidades do sistema.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start">
                  <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="text-sm font-medium text-blue-800 mb-1">
                      Próximo Passo
                    </h3>
                    <p className="text-sm text-blue-700">
                      Clique em "Continuar" para ser redirecionado automaticamente para o painel do CMS.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex justify-center pt-2">
                <Button
                  onClick={handleSuccessContinue}
                  className="w-full h-10 bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Continuar para o CMS
                </Button>
              </div>
            </div>
          )}
        </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default XuiMigrationModal;
